import Bugsnag from '@bugsnag/js';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as Actions from '../../../actions';
import { getInDomain, noopWithErrorLog } from '../../../domains/externalUtils';
import { putAndWait } from '../../../sagas/TaskManager';
import * as Selectors from '../../../selectors';
import Logger from '../../../tools/Logger';
import * as Utils from '../../../utils';

const log = Logger.child({
  component: 'mediator/autoLogin',
});

const ROOT = Utils.Route.getRootPath();

// HELPERS

/**
 * Returns the fixed up path taking into account the application root
 *
 * @param {string} path - path to evaluate
 * @returns {string} fixed path
 */
export function* getRedirectPath() {
  const routing = yield select(Utils.Route.getRouterCurrentLocation);
  const queryString = routing.search ? routing.search : '';

  const path = routing.pathname + queryString;

  if (path.indexOf(ROOT) === 0) {
    return path.substr(ROOT.length - 1);
  }
  return path;
}

// @todo - WEBCLIENT-9590 Is there a better way to prevent loginRedirectOnSuccess from being cleared?
// This handles a special case where we want to a listener
// who enters the partner upgrade flow to be able to refresh their page and not lose the
// loginRedirectOnSuccess.
export function* isPartnerUpgradeSpecialCase() {
  const isPartnerUpgradeFlow = yield select(Selectors.Payments.isPartnerUpgradeFlow);
  const loginSuccessRedirect = yield select(Utils.Listener.getLoginRedirectOnSuccess);
  const isAnon = yield select(Utils.Listener.isAnonymousListener);
  return isPartnerUpgradeFlow && isAnon && loginSuccessRedirect;
}

// @todo - WEBCLIENT-9590 same issue with regular upgrades
export function* isRegularUpgradeSpecialCase() {
  const selectedTier = yield select(Selectors.Payments.getSelectedTier);
  const selectedDiscountType = yield select(Selectors.Payments.getSelectedDiscountType);
  const loginSuccessRedirect = yield select(Utils.Listener.getLoginRedirectOnSuccess);
  return selectedTier && selectedDiscountType && loginSuccessRedirect;
}

/**
 * Logs in a previously logged in registered listener
 *
 * @param {string} username - the username to authenticate
 * @param {boolean} rememberMe - whether or not to remember login
 */
export function* loginRegistered(username, rememberMe) {
  log.info('loginAsRegistered(): logging in previously logged in registered listener');

  const authToken = yield select(Utils.Listener.getAuthToken);

  yield call(
    putAndWait,
    Actions.Login.authenticateListener({
      isLoginAttempt: false,
      isAnon: false,
      authToken,
      username,
      rememberMe,
      isUserInitiated: false,
      isCustomerServiceLogin: false,
    }),
  );
}

/**
 * Logs in a listener with a provided auth token
 *
 * @param {string} csAuthToken - the auth token of the listener to log in
 */
export function* loginWithCSToken(csAuthToken) {
  log.info('loginWithCSToken(): logging user in with provided provided auth token');

  yield call(
    putAndWait,
    Actions.Login.authenticateListener({
      isLoginAttempt: true,
      isAnon: true,
      authToken: csAuthToken,
      rememberMe: false,
      isUserInitiated: false,
      isCustomerServiceLogin: true,
    }),
  );
}

/**
 * Logs in a listener with a provided access token
 *
 * @param {string} accessToken - the access token of the listener to log in
 */
export function* loginWithAccessToken(accessToken) {
  log.info('loginWithCSToken(): logging user in with provided provided access token');

  yield call(
    putAndWait,
    Actions.Login.authenticateListener({
      isLoginAttempt: true,
      isAnon: false,
      accessToken,
      rememberMe: true,
      isUserInitiated: false,
    }),
  );
}

/**
 * Logs in from legacy Windows 10 app to new Windows desktop app
 *
 * @param {string} deviceUuid - the device uuid used to pass into the deviceLogin API
 */
export function* loginWithDeviceUuid(deviceUuid) {
  log.info('loginWithDeviceUuid(): logging user in with provided device uuid');
  yield call(
    putAndWait,
    Actions.Login.authenticateListener({
      isLoginAttempt: true,
      isAnon: false,
      rememberMe: true,
      isUserInitiated: false,
      deviceUuid,
    }),
  );
}

/**
 * Logs in an anonymous listener
 */
export function* loginAnonymous() {
  log.info('loginAnonymous(): logging in as anonymous listener');

  yield call(
    putAndWait,
    Actions.Login.authenticateListener({
      isLoginAttempt: true,
      isAnon: true,
      rememberMe: false,
      isUserInitiated: false,
      isCustomerServiceLogin: false,
    }),
  );
}

/**
 * Handle post auth logic such as redirecting to the next page in the app
 */
export function* handlePostAuth() {
  const isPartnerUpgrade = yield call(isPartnerUpgradeSpecialCase);
  const isRegularUpgrade = yield call(isRegularUpgradeSpecialCase);
  const isSpecialCase = isPartnerUpgrade || isRegularUpgrade;

  let nextPath;
  if (!isSpecialCase) {
    // @todo - WEBCLIENT-9590
    const afterLoginRedirect = yield call(getRedirectPath);
    yield put(Actions.Login.setAfterLoginRedirect(afterLoginRedirect));

    nextPath = yield select(Utils.Route.getNextPath, false);
    if (nextPath === window.location.pathname && window.location.hash) {
      nextPath += window.location.hash;
    }
  }

  const isPlaybackEnabled = yield select(Utils.Capabilities.isPlaybackEnabled);
  const isAutoStartEnabled = yield select(Utils.Capabilities.isAutoStartEnabled);
  const isLoggedIn = yield select(Utils.Listener.isLoggedIn);
  const isInAntiAdBlockHammerMode = yield select(
    getInDomain('application.utils.isAntiAdBlockerHammerModeDetected'),
  );
  if (isLoggedIn && isPlaybackEnabled && isAutoStartEnabled && !isInAntiAdBlockHammerMode) {
    yield put(getInDomain('playback.actions.setAutoStartEnabled', noopWithErrorLog)(true));
  }

  // Start the Bugsnag session if logged in
  if (isLoggedIn) {
    Bugsnag.startSession();
  }

  // clear post auth redirect
  yield put(Actions.Login.loginFlowComplete(isSpecialCase));

  // redirect to post auth path
  if (!isSpecialCase) {
    yield put(push(nextPath));
  }
}

/**
 * Automatically log in a listener without user interaction. There are
 * several scenarios where this can happen.
 *
 * 1) A previously registered logged in listener
 * 2) A customer service rep logging in as another listener via a csAuthToken supplied via querystring
 * 3) A listener logged in via access token provided via querystring
 * 4) An anonymous listener
 *
 * @param {object} action - the provided use case action
 */
export function* autoLogin(action) {
  log.info('autoLogin(): attempting auto login');

  const csAuthToken = yield select(Utils.TunerVars.getCustomerServiceAuthToken);
  const accessToken = yield select(Utils.QueryParams.getAccessToken);
  const username = yield select(Utils.Listener.getCurrentUsername);
  const isRememberMe = yield select(Utils.Listener.isRememberMe);
  const deviceUuidIfMetRequirement =
    Utils.ClientInfo.getDeviceUuidIfMetWindowsDesktopAppAccountMigrationRequirement();

  try {
    // auto login scenarios ordered by priority
    if (csAuthToken) {
      yield call(loginWithCSToken, csAuthToken);
    } else if (accessToken) {
      yield call(loginWithAccessToken, accessToken);
    } else if (deviceUuidIfMetRequirement) {
      yield call(loginWithDeviceUuid, deviceUuidIfMetRequirement);
    } else if (username && isRememberMe) {
      yield call(loginRegistered, username, isRememberMe);
    } else {
      yield call(loginAnonymous);
    }

    // handle post auth logic
    yield call(handlePostAuth);
  } catch (err) {
    log.info('autoLogin(): login attempt failed');
    log.error(err);
  }
}
